import type {
  FetchArgs,
  FetchBaseQueryError,
  FetchBaseQueryMeta,
} from "@reduxjs/toolkit/query"
import { advApi } from "./advApi"
import type {
  ContentBinding,
  ContentFilters,
  ContentsListReq,
  ContentsListRes,
  CreateBindingReq,
  CreateBindingRes,
  CreateContentReq,
  CreateContentRes,
  DeleteBindingReq,
  DeleteBindingRes,
  DeleteContentReq,
  DeleteContentRes,
  GetAssetLinkReq,
  GetAssetLinkRes,
  GetContentBindingsReq,
  GetContentBindingsRes,
  GetContentReq,
  GetContentRes,
  GetContentsFiltersReq,
  GetContentsFiltersRes,
  ReplyMarkup,
} from "../interfaces/contents"
import {
  contentMutationInvalidates,
  isContentUploadError,
  uploadContent,
} from "../services/contentUpload"

export const CONTENT_TAG_TYPES = ["Contents", "ContentsFilters"] as const

export const CONTENTS_GROUP_CAP = 100
export const CONTENTS_LIMIT_DEFAULT = 50

class ContentValidationError extends Error {}

// Локальная ошибка RTK, без выдуманного HTTP-статуса/кода backend.
// Проверяем построение запроса внутри queryFn, чтобы ожидаемое отклонение
// не становилось необработанной ошибкой RTK из query.
const validateContentsRequest = (
  buildRequest: () => FetchArgs,
): { request: FetchArgs } | { error: FetchBaseQueryError } => {
  try {
    return { request: buildRequest() }
  } catch (error) {
    if (error instanceof ContentValidationError) {
      return { error: { status: "CUSTOM_ERROR", error: error.message } }
    }
    throw error
  }
}

const pathId = (id: number): number => {
  if (!Number.isSafeInteger(id) || id <= 0) {
    throw new ContentValidationError(
      "ID должен быть безопасным целым числом больше 0",
    )
  }
  return id
}

const isLocalValidationError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "status" in error &&
  error.status === "CUSTOM_ERROR"

// buildContentsQuery: только URLSearchParams, приклеенный к URL конкатенацией
// (`contents?${q}`) — FetchArgs.params (Record<string, any>) свернул бы
// повторяющиеся ключи и потребовал any. Пустые группы и незаданные даты в URL
// не попадают; limit/offset ставятся всегда; page не существует (§4.2).
export const buildContentsQuery = (
  filters: ContentFilters,
  limit: number,
  offset: number,
): string => {
  if (!Number.isSafeInteger(limit) || limit <= 0) {
    throw new ContentValidationError(
      `limit должен быть целым числом больше 0, получено: ${limit}`,
    )
  }
  if (!Number.isSafeInteger(offset) || offset < 0) {
    throw new ContentValidationError(
      `offset должен быть неотрицательным целым числом, получено: ${offset}`,
    )
  }
  if (
    filters.orderDateFrom &&
    filters.orderDateTo &&
    filters.orderDateFrom > filters.orderDateTo
  ) {
    throw new ContentValidationError("Дата «с» не может быть позже даты «по»")
  }
  const params = new URLSearchParams()
  const groups: Array<{
    filterKey: "campaigns" | "orders" | "agents"
    urlKey: string
  }> = [
    { filterKey: "campaigns", urlKey: "campaigns" },
    { filterKey: "orders", urlKey: "orders" },
    { filterKey: "agents", urlKey: "agents" },
  ]
  for (const group of groups) {
    const unique = Array.from(new Set(filters[group.filterKey] ?? []))
    if (unique.length === 0) {
      continue
    }
    if (unique.length > CONTENTS_GROUP_CAP) {
      throw new ContentValidationError(
        `В группе «${group.urlKey}» не может быть больше 100 ID`,
      )
    }
    for (const id of unique) {
      params.append(group.urlKey, String(id))
    }
  }
  if (filters.orderDateFrom) {
    params.append("order_date_from", filters.orderDateFrom)
  }
  if (filters.orderDateTo) {
    params.append("order_date_to", filters.orderDateTo)
  }
  params.append("limit", String(limit))
  params.append("offset", String(offset))
  return params.toString()
}

// Все пути плоские — владельца больше нет (contentsOwnerPath из v1 не переносится).
export const contentsPath = (contentId: number): string =>
  `/contents/${pathId(contentId)}`

// ВАЖНО: второй createApi не создаётся; registerReducer/middleware повторять
// не нужно — они уже выполнены в advApi.ts. enhanceEndpoints мутирует advApi
// и возвращает его же (теги Contents/ContentsFilters добавляются в рантайме).
const contentsApiQueries = advApi
  .enhanceEndpoints({
    addTagTypes: CONTENT_TAG_TYPES,
    endpoints: {
      // getOrder сейчас даёт безымянный тег "Order"; добавляем id-тег,
      // сохранив старый для совместимости с updateOrder/invalidatesTags.
      getOrder: {
        providesTags: (_result, _error, arg) => [
          "Order",
          { type: "Order", id: arg.pathParams.id },
        ],
      },
    },
  })
  .injectEndpoints({
    endpoints: (build) => ({
      getContents: build.query<ContentsListRes, ContentsListReq>({
        queryFn: async ({ queryParams }, _api, _extraOptions, baseQuery) => {
          const validated = validateContentsRequest(() => ({
            url: `/contents?${buildContentsQuery(
              queryParams,
              queryParams.limit ?? CONTENTS_LIMIT_DEFAULT,
              queryParams.offset ?? 0,
            )}`,
            method: "GET",
          }))
          if ("error" in validated) return validated
          const result = await baseQuery(validated.request)
          if (result.error) return { error: result.error }
          return { data: result.data as ContentsListRes, meta: result.meta }
        },
        providesTags: [{ type: "Contents", id: "LIST" }],
      }),
      getContentsFilters: build.query<
        GetContentsFiltersRes,
        GetContentsFiltersReq
      >({
        query: () => ({
          url: "contents/filters",
          method: "GET",
        }),
        providesTags: ["ContentsFilters"],
      }),
      getContent: build.query<GetContentRes, GetContentReq>({
        queryFn: async ({ pathParams }, _api, _extraOptions, baseQuery) => {
          const validated = validateContentsRequest(() => ({
            url: contentsPath(pathParams.contentId),
            method: "GET",
          }))
          if ("error" in validated) return validated
          const result = await baseQuery(validated.request)
          if (result.error) return { error: result.error }
          return { data: result.data as GetContentRes, meta: result.meta }
        },
        providesTags: (result, error, arg) => [
          { type: "Contents", id: arg.pathParams.contentId },
        ],
      }),
      // Свежесть ссылок определяет общий TTL-кэш, не RTK Query.
      // Хук query намеренно не экспортируется: используйте assetLinkCache.
      getAssetLink: build.query<GetAssetLinkRes, GetAssetLinkReq>({
        queryFn: async ({ pathParams }, _api, _extraOptions, baseQuery) => {
          const validated = validateContentsRequest(() => ({
            url: `${contentsPath(pathParams.contentId)}/assets/${pathId(pathParams.assetId)}/download`,
            method: "GET",
          }))
          if ("error" in validated) return validated
          const result = await baseQuery(validated.request)
          if (result.error) return { error: result.error }
          return { data: result.data as GetAssetLinkRes, meta: result.meta }
        },
        providesTags: (result, error, arg) => [
          { type: "Contents", id: arg.pathParams.contentId },
        ],
      }),
      getContentBindings: build.query<
        GetContentBindingsRes,
        GetContentBindingsReq
      >({
        queryFn: async ({ pathParams }, _api, _extraOptions, baseQuery) => {
          const validated = validateContentsRequest(() => ({
            url: `${contentsPath(pathParams.contentId)}/bindings`,
            method: "GET",
          }))
          if ("error" in validated) return validated
          const result = await baseQuery(validated.request)
          if (result.error) return { error: result.error }
          return {
            data: result.data as GetContentBindingsRes,
            meta: result.meta,
          }
        },
        providesTags: (result, error, arg) => [
          { type: "Contents", id: arg.pathParams.contentId },
        ],
      }),
    }),
  })

export const {
  useGetContentsQuery,
  useGetContentsFiltersQuery,
  useGetContentQuery,
  useGetContentBindingsQuery,
} = contentsApiQueries

const contentMutations = contentsApiQueries.injectEndpoints({
  endpoints: (build) => ({
    createContent: build.mutation<CreateContentRes, CreateContentReq>({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const { files, text, assetIds, replyMarkup, campaignIds, orderIds } =
          arg.body
        // JSON-ветка — нет НОВЫХ файлов, даже если есть переиспользуемые (§4.3).
        if (!files || files.length === 0) {
          const body: {
            text?: string
            asset_ids?: number[]
            reply_markup?: ReplyMarkup
            campaign_ids?: number[]
            order_ids?: number[]
          } = {}
          if (text !== undefined && text.trim() !== "") {
            body.text = text
          }
          if (assetIds && assetIds.length > 0) {
            body.asset_ids = assetIds
          }
          if (replyMarkup && replyMarkup.inline_keyboard.length > 0) {
            body.reply_markup = replyMarkup
          }
          if (campaignIds && campaignIds.length > 0) {
            body.campaign_ids = campaignIds
          }
          if (orderIds && orderIds.length > 0) {
            body.order_ids = orderIds
          }
          const signal = arg.signal ?? api.signal
          const result = await baseQuery({
            url: "/contents",
            method: "POST",
            body,
            signal,
          })
          if (result.error) {
            if (signal.aborted) {
              return { error: { status: 0, data: { code: "aborted" } } }
            }
            return { error: result.error }
          }
          return { data: result.data as CreateContentRes }
        }
        try {
          const data = await uploadContent({
            text,
            files,
            assetIds: assetIds ?? [],
            replyMarkup,
            campaignIds: campaignIds ?? [],
            orderIds: orderIds ?? [],
            onProgress: arg.onUploadProgress,
            signal: arg.signal ?? api.signal,
          })
          return { data }
        } catch (err) {
          if (isContentUploadError(err)) {
            return {
              error: {
                status: err.status,
                data: {
                  error: err.error,
                  ...(err.code !== undefined ? { code: err.code } : {}),
                },
              },
            }
          }
          return {
            error: { status: 0, data: { error: "Нет соединения с сервером" } },
          }
        }
      },
      invalidatesTags: (result, error, arg) =>
        contentMutationInvalidates({
          campaignIds: arg.body.campaignIds,
          orderIds: arg.body.orderIds,
        }),
    }),
    deleteContent: build.mutation<DeleteContentRes, DeleteContentReq>({
      queryFn: async ({ pathParams }, _api, _extraOptions, baseQuery) => {
        const validated = validateContentsRequest(() => ({
          url: contentsPath(pathParams.contentId),
          method: "DELETE",
        }))
        if ("error" in validated) return validated
        const result = await baseQuery(validated.request)
        // 204 тела не имеет (§4.1).
        return result.error ? { error: result.error } : { data: null }
      },
      // Набор привязок удаляемого материала фронтенду неизвестен → все Order-теги.
      invalidatesTags: (result, error, arg) =>
        isLocalValidationError(error)
          ? []
          : [
              { type: "Contents", id: arg.pathParams.contentId },
              { type: "Contents", id: "LIST" },
              "ContentsFilters",
              "Orders",
              { type: "Order" },
            ],
    }),
  }),
})

const bindingMutations = contentMutations.injectEndpoints({
  endpoints: (build) => ({
    // queryFn на baseQuery: нужен HTTP-статус, чтобы различить 201/200 (§10.6).
    createBinding: build.mutation<CreateBindingRes, CreateBindingReq>({
      queryFn: async (arg, api, extraOptions, baseQuery) => {
        const validated = validateContentsRequest(() => ({
          url: `${contentsPath(arg.pathParams.contentId)}/bindings`,
          method: "POST",
          body: arg.body,
        }))
        if ("error" in validated) return validated
        const result = await baseQuery(validated.request)
        if (result.error) {
          return { error: result.error }
        }
        const meta = result.meta as FetchBaseQueryMeta | undefined
        const existing = meta?.response?.status === 200
        return {
          data: { binding: result.data as ContentBinding, existing },
        }
      },
      invalidatesTags: (result, error, arg) =>
        isLocalValidationError(error)
          ? []
          : [
              { type: "Contents", id: arg.pathParams.contentId },
              { type: "Contents", id: "LIST" },
              "ContentsFilters",
              "Orders",
              ...("order_id" in arg.body
                ? [{ type: "Order" as const, id: arg.body.order_id }]
                : [{ type: "Order" as const }]),
            ],
    }),
    // body — только метаданные для invalidatesTags; на сервер не уходит
    // (queryFn деструктурирует только pathParams).
    deleteBinding: build.mutation<DeleteBindingRes, DeleteBindingReq>({
      queryFn: async ({ pathParams }, _api, _extraOptions, baseQuery) => {
        const validated = validateContentsRequest(() => ({
          url: `${contentsPath(pathParams.contentId)}/bindings/${pathId(pathParams.bindingId)}`,
          method: "DELETE",
        }))
        if ("error" in validated) return validated
        const result = await baseQuery(validated.request)
        // 204 тела не имеет (§4.1).
        return result.error ? { error: result.error } : { data: null }
      },
      invalidatesTags: (result, error, arg) =>
        isLocalValidationError(error)
          ? []
          : [
              { type: "Contents", id: arg.pathParams.contentId },
              { type: "Contents", id: "LIST" },
              "ContentsFilters",
              "Orders",
              ...("order_id" in arg.body
                ? [{ type: "Order" as const, id: arg.body.order_id }]
                : [{ type: "Order" as const }]),
            ],
    }),
  }),
})

export const contentsApi = bindingMutations

export const { useCreateContentMutation, useDeleteContentMutation } =
  contentMutations

export const { useCreateBindingMutation, useDeleteBindingMutation } =
  bindingMutations

export {
  contentMutationInvalidates,
  type ContentMutationTag,
  type ContentMutationInvalidatesArgs,
} from "../services/contentUpload"
